import { confirmUnsavedChanges } from "./confirmUnsavedChanges";

export function unsavedChangesSessionController($transitions, $window) {
	const ctrl = this;
	const participants = [];

	ctrl.initialize = initialize;
	ctrl.destroy = destroy;
	ctrl.register = register;
	ctrl.isDirty = isDirty;
	ctrl.checkIfDirty = checkIfDirty;
	ctrl.deregisterTransitionHook = () => {};
	ctrl.isInitialized = false;

	function initialize() {
		if(ctrl.isInitialized) {
			return;
		}

		ctrl.isInitialized = true;
		$window.addEventListener("beforeunload", alertUserIfDirty);
		ctrl.deregisterTransitionHook = $transitions.onBefore({}, () => {
			return ctrl.checkIfDirty({ prompt: true }).then(confirmed => {
				if(!confirmed) {
					return false;
				}
			});
		});
	}

	function destroy() {
		if(!ctrl.isInitialized) {
			return;
		}

		ctrl.deregisterTransitionHook();
		$window.removeEventListener("beforeunload", alertUserIfDirty);
		ctrl.isInitialized = false;
	}

	function register(participant) {
		validateParticipant(participant);
		participants.push(participant);

		return function deregister() {
			const index = participants.indexOf(participant);
			if(index !== -1) {
				participants.splice(index, 1);
			}
		};
	}

	function isDirty() {
		return participants.some(participant => participant.isDirty());
	}

	async function checkIfDirty(options = {}) {
		if(!ctrl.isDirty()) {
			return true;
		}

		if(!options.prompt) {
			return false;
		}

		return confirmUnsavedChanges();
	}

	function alertUserIfDirty(e) {
		if(ctrl.isDirty()) {
			e.preventDefault();
			e.returnValue = "";
		}
	}

	function validateParticipant(participant) {
		if(!participant || typeof participant.isDirty !== "function") {
			throw new Error("unsaved-changes-participant requires an isDirty() participant.");
		}
	}
}

export function unsavedChangesSessionDirective($parse) {
	return {
		restrict: "A",
		controller: unsavedChangesSessionController,
		require: "unsavedChangesSession",
		link(scope, element, attrs, session) {
			session.initialize();

			const assignSession = attrs.unsavedChangesSession
				? $parse(attrs.unsavedChangesSession).assign
				: undefined;

			if(attrs.unsavedChangesSession && !assignSession) {
				throw new Error("unsaved-changes-session must be assignable when a value is provided.");
			}

			if(assignSession) {
				assignSession(scope, session);
			}

			scope.$on("$destroy", () => {
				if(assignSession) {
					assignSession(scope, undefined);
				}
				session.destroy();
			});
		}
	};
}

export function unsavedChangesParticipantController($attrs, $element, $parse, $scope) {
	const ctrl = this;

	ctrl.deregister = () => {};
	ctrl.$postLink = registerParticipant;
	ctrl.$onDestroy = onDestroy;

	function registerParticipant() {
		const component = resolveParticipantComponent();
		if(!component || typeof component.isDirty !== "function") {
			throw new Error("unsaved-changes-participant requires an isDirty() participant.");
		}

		const participant = {
			isDirty: () => component.isDirty(),
			save: typeof component?.save === "function"
				? () => component.save()
				: undefined
		};

		ctrl.deregister = ctrl.session.register(participant);
	}

	function resolveParticipantComponent() {
		if($attrs.unsavedChangesParticipant) {
			return $parse($attrs.unsavedChangesParticipant)($scope);
		}

		const elementData = $element.data();
		const controllers = Object.keys(elementData)
			.filter(key => key.endsWith("Controller"))
			.map(key => elementData[key])
			.filter(controller => controller !== ctrl)
			.filter(controller => controller !== ctrl.session)
			.filter(controller => controller && typeof controller.isDirty === "function");

		if(controllers.length > 1) {
			throw new Error("unsaved-changes-participant found multiple isDirty() participants.");
		}

		return controllers[0];
	}

	function onDestroy() {
		ctrl.deregister();
		ctrl.deregister = () => {};
	}
}

export function unsavedChangesParticipantDirective() {
	return {
		restrict: "A",
		require: {
			session: "^^unsavedChangesSession"
		},
		bindToController: true,
		controller: unsavedChangesParticipantController
	};
}

export default angular.module("vesl.components.unsaved-changes", [])
	.directive("unsavedChangesSession", unsavedChangesSessionDirective)
	.directive("unsavedChangesParticipant", unsavedChangesParticipantDirective)
	.name;
